/**
 * Defense-in-depth: strip leaked tool-call/JSON/planning text from the
 * assistant's user-visible reply. Reasoning-style models (gpt-oss, etc.)
 * sometimes dump their plan + the tool-call JSON + a simulated tool output
 * into the text channel instead of routing the tool call through the proper
 * channel. The system prompt forbids it explicitly, but compliance is not
 * 100% — this function is the safety net.
 *
 * Conservative on purpose: only strips patterns that have NO legitimate use
 * in a Funkey assistant reply (the system prompt mandates plain prose with no
 * braces / code fences / planning verbs).
 *
 * Exported for unit tests; called by the panel before passing `part.text` to
 * Streamdown.
 */
/**
 * JSON keys from the `search_activities` tool input/output that have NO
 * legitimate reason to appear in assistant prose. Finding any one of these
 * (quoted, with a colon) means the model leaked schema fragments into the
 * text channel and everything from that point is suspect.
 *
 * Curly-quote (`"…":`) and straight-quote (`"…":`) variants both matched.
 */
const LEAK_KEY_PATTERNS = [
    'tool',
    'arguments',
    'query',
    'expandedQuery',
    'audience',
    'themes',
    'vibe',
    'locationType',
    'locale',
    'participants',
    'minBudget',
    'maxBudget',
    'limit',
    'excludeKeywords',
    'excludeFamilies',
    'vatMode',
    'count',
    'requestedLimit',
    'appliedVatMode',
    'summaryByLocale',
    'activityIds',
    'topActivities',
    'minPartySize',
    'maxPartySize',
    'imageUrl',
    'priceFrom',
    'priceOnRequest',
    'filters',
    'summary',
];

const LEAK_KEY_RE = new RegExp(`["“”](?:${LEAK_KEY_PATTERNS.join('|')})["“”]\\s*:`, 'i');

export function sanitizeAssistantText(text: string): string {
    if (text.length === 0) return text;

    let out = text;

    // 1) Strip fenced code blocks (```...```) — assistant prose has none.
    out = out.replace(/```[\s\S]*?```/g, '');
    // Unterminated fenced block during a partial stream — cut from the fence on.
    out = out.replace(/```[\s\S]*$/g, '');

    // 2) Strip balanced curly-brace blocks (tool-call / tool-output JSON).
    //    Repeatedly remove the innermost `{...}` until no balanced block remains.
    //    The system prompt forbids `{`/`}` in replies, so this is safe.
    for (let i = 0; i < 10; i++) {
        const next = out.replace(/\{[^{}]*\}/g, '');
        if (next === out) break;
        out = next;
    }

    // 3) Schema-key leak: if any known tool-input/output key appears (e.g.
    //    `"arguments":`, `"topActivities":`), the model is bleeding JSON
    //    fragments — often without surrounding braces because streaming or
    //    formatting ate them. Cut from the FIRST such marker to the end of
    //    the text. Anything legit must precede the leak. Belt-and-suspenders
    //    over the brace strip in step 2.
    const leakMatch = out.match(LEAK_KEY_RE);
    if (leakMatch && leakMatch.index !== undefined) {
        // Walk back to the previous sentence boundary so we don't leave a
        // dangling fragment ("Er zijn er maar 34 die …; 25").
        const cut = out.slice(0, leakMatch.index);
        const lastBoundary = Math.max(
            cut.lastIndexOf('. '),
            cut.lastIndexOf('! '),
            cut.lastIndexOf('? '),
            cut.lastIndexOf('.\n'),
            cut.lastIndexOf('\n'),
        );
        out = lastBoundary >= 0 ? cut.slice(0, lastBoundary + 1) : cut;
    }

    // 4) Unterminated `{` left over from a mid-stream partial JSON dump — cut.
    const firstBrace = out.indexOf('{');
    if (firstBrace !== -1) out = out.slice(0, firstBrace);

    // 5) Strip known planning / meta-commentary phrases. Two categories:
    //    a) Tool-call planning verbs (original set)
    //    b) Reasoning-leaked-into-text patterns ("The user continues with...",
    //       "We need to ask for clarification", "Provide a short sentence",
    //       "Likely they are...", "No clear request") — these indicate the model
    //       dumped its chain-of-thought into the text channel instead of the
    //       reasoning channel.
    const planningPhrases = [
        /\bWe (need|have) to (call|simulate) the tool\b[^.!?\n]*[.!?]?/gi,
        /\bWe(?:'| wi)ll (call|invoke|simulate) the tool\b[^.!?\n]*[.!?]?/gi,
        /\b(Let'?s|I (will|should|need to)) (call|invoke) (the )?(tool|function)\b[^.!?\n]*[.!?]?/gi,
        /\b(Simulated|Here is the) tool output\b[^.!?\n]*[.!?]?/gi,
        /\bfunctions\.search_activities\b/gi,
        // Reasoning meta-commentary
        /\bThe user (continues|said|typed|entered|provided|sent|is saying)\b[^.!?\n]*[.!?]?/gi,
        /\b(No|Not a?) clear (request|query|input|intent)\b[^.!?\n]*[.!?]?/gi,
        /\bLikely they (are|were|have)\b[^.!?\n]*[.!?]?/gi,
        /\bWe need to (ask|clarify|request|probe)\b[^.!?\n]*[.!?]?/gi,
        /\bProvide a (short|brief|concise)? ?(sentence|response|reply|answer)\b[^.!?\n]*[.!?]?/gi,
        /\b(This|That) (is|looks like|seems like) (a )?(gibberish|meaningless|unclear|random|nonsensical)\b[^.!?\n]*[.!?]?/gi,
        /\bI (should|will|must|need to) (ask|clarify|request more)\b[^.!?\n]*[.!?]?/gi,
    ];
    for (const re of planningPhrases) out = out.replace(re, '');

    // 6) Collapse runs of whitespace introduced by the strips.
    out = out
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    // 7) De-duplicate a reply the model emitted TWICE in one text part (a gpt-oss
    //    habit — e.g. "…€45 p.p.Ik vond …€45 p.p."). Collapse `X` + optional
    //    whitespace + `X` back to a single `X`.
    const doubled = out.match(/^([\s\S]+?)\s*\1$/);
    if (doubled?.[1] && doubled[1].trim().length > 0) out = doubled[1].trim();

    // 7b) De-duplicate a NEAR-doubled lead — the model emits a warning as its own
    //     sentence and then REPEATS it as the lead of the summary sentence (the
    //     "warning must lead" + "single count sentence" prompt rules combined):
    //     "…beste matches.Let op: …beste matches voor 60 personen in Gent." Step 7
    //     misses it because the two copies are not identical (the second carries the
    //     count/constraints). When the first sentence is a verbatim PREFIX of what
    //     immediately follows, drop the first and keep the fuller one. The two copies
    //     often abut with NO space ("matches.Let op"), so the boundary also breaks on
    //     a terminator directly followed by an uppercase letter (not just whitespace).
    const leadMatch = out.match(/^([\s\S]+?[.!?])\s*(?=[\p{Lu}])/u);
    if (leadMatch?.[1]) {
        const firstSentence = leadMatch[1].slice(0, -1).trim();
        const rest = out.slice(leadMatch[0].length).trim();
        if (firstSentence.length > 0 && rest.startsWith(firstSentence)) out = rest;
    }

    return out;
}
